import { SearchIcon } from '@chakra-ui/icons';
import {
  useDisclosure,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Input,
  Text,
} from '@chakra-ui/react';
import { FixedSizeList } from 'react-window';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Shimmer } from 'components/Shimmer';
import { STATUS } from 'projectConstants';
import { getDashboardBlocks, getDashboardBlockStatus } from 'redux/DashboardSliceV2';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';

export interface Properties {
  blockId: number;
  indicatorId: number;
  handleIndicatorChange: (indicator: any) => void;
}
const ModalIndicator = ({ blockId, indicatorId, handleIndicatorChange }: Properties) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const blockDetails = useSelector(getDashboardBlocks);
  const indicatorStatus = useSelector(getDashboardBlockStatus);
  const inputReference: any = useRef(null);
  const [listData, setListData] = useState([]);
  const [indicatorDetails, setIndicatorDetails] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const itemHeight = 30;
  const calculatedHeight = listData.length * itemHeight;
  const height = Math.min(calculatedHeight, 150);
  const width = 260;

  const selectedIndicator = useMemo(() => {
    if (indicatorId && indicatorDetails?.length) {
      const blockData: any = indicatorDetails?.find(
        (item: any) => item.indicator_id === indicatorId,
      );
      return blockData?.indicator_name;
    }
    return null;
  }, [indicatorId, indicatorDetails]);

  useEffect(() => {
    if (blockId) {
      const selectedBlock = blockDetails?.find((block: any) => block.block_id === blockId);
      setIndicatorDetails(selectedBlock?.indicators?.length ? selectedBlock?.indicators : []);
      setListData(selectedBlock?.indicators?.length ? selectedBlock?.indicators : []);
    } else {
      setListData([]);
    }
  }, [blockId, blockDetails]);

  const handleSearch = (value: string) => {
    setListData(
      indicatorDetails?.filter((item: any) =>
        item.indicator_name?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
    setSearchValue(value);
  };

  const Row = ({ index, style, data }: any) => {
    const handleItemClick = () => {
      handleIndicatorChange(data[index]);
      onClose();
    };

    return (
      <Flex
        alignItems={'center'}
        bg='white'
        mb='1px'
        px='11px'
        fontFamily='Roboto Regular'
        fontSize='13px'
        color='#121826'
        _hover={{ backgroundColor: '#F5F5F5' }}
        onClick={handleItemClick}
        style={style}
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        cursor={'pointer'}
      >
        {data[index].indicator_name}
      </Flex>
    );
  };
  return (
    <Flex flexDir={'column'} gap='4px'>
      <Text fontFamily='Roboto Bold' fontWeight='600' fontSize='13px' color='#121826'>
        Indicator
      </Text>
      <Menu isOpen={isOpen} onClose={onClose} strategy='fixed' isLazy matchWidth>
        <MenuButton width={'100%'} onClick={isOpen ? onClose : onOpen}>
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            backgroundColor='white'
            border='1px solid #D9D9D9'
            px='11px'
            py='11px'
            borderRadius='9px'
            color={selectedIndicator ? '#121826' : '#708090'}
            fontFamily='Roboto Regular'
            fontSize='13px'
          >
            <Text>{selectedIndicator || 'Name here'}</Text>
            <FigmaTriangleDown width={10} height={6} color='#708090' />
          </Flex>
        </MenuButton>
        <MenuList
          p={0}
          bg='#E8E9EB'
          border='none'
          borderRadius='14px'
          overflow='hidden'
          boxShadow='0px 4px 24px rgba(0, 0, 0, 0.25)'
        >
          {blockDetails ? (
            <>
              {indicatorStatus === STATUS.LOADING ? (
                <MenuItem
                  as='div'
                  role='status'
                  aria-label='Loading indicators'
                  bg='white'
                  p='12px'
                >
                  <Shimmer height='10px' width='100%' mb='8px' />
                  <Shimmer height='10px' width='75%' mb='8px' />
                  <Shimmer height='10px' width='85%' />
                </MenuItem>
              ) : (
                <>
                  <Flex alignItems={'center'} bg='white' mb='1px' px='11px' height='40px'>
                    <InputGroup>
                      <InputLeftElement
                        pointerEvents='none'
                        h='100%'
                        children={<SearchIcon width={'14px'} height={'14px'} color='#708090' />}
                      />
                      <Input
                        border='none'
                        fontFamily='Roboto Regular'
                        fontSize='13px'
                        color='#121826'
                        _placeholder={{ color: '#BEBEBE' }}
                        _focus={{ boxShadow: 'none' }}
                        ref={inputReference}
                        placeholder='Search'
                        onChange={(event: any) => {
                          handleSearch(event.target.value);
                        }}
                      />
                    </InputGroup>
                  </Flex>
                  {indicatorDetails?.length > 0 ? (
                    <FixedSizeList
                      className='scroll'
                      height={height}
                      itemData={listData}
                      itemCount={listData?.length}
                      itemSize={itemHeight}
                      width={width}
                    >
                      {Row}
                    </FixedSizeList>
                  ) : (
                    <Text bg='white' px='11px' py='9px' fontFamily='Roboto Regular' fontSize='13px'>
                      No Indicator found
                    </Text>
                  )}
                </>
              )}
            </>
          ) : (
            <Text bg='white' px='11px' py='9px' fontFamily='Roboto Regular' fontSize='13px'>
              Select Block
            </Text>
          )}
        </MenuList>
      </Menu>
    </Flex>
  );
};
export default ModalIndicator;
